import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import WorkerLayout from '../../layouts/WorkerLayout';
import Button from '../../components/common/Button';
import toast from 'react-hot-toast';

const URGENCY_STYLE = {
  critical: 'bg-red-100 text-red-700 border border-red-200',
  high:     'bg-orange-100 text-orange-700 border border-orange-200',
  medium:   'bg-yellow-100 text-yellow-700 border border-yellow-200',
  low:      'bg-gray-100 text-gray-600 border border-gray-200',
};

function InfoRow({ icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
      <span className="text-lg flex-shrink-0">{icon}</span>
      <div>
        <p className="text-xs text-gray-400">{label}</p>
        <p className="text-sm font-medium text-gray-800">{value}</p>
      </div>
    </div>
  );
}

export default function WorkerJobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [noteModal, setNoteModal] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => {
    api.get(`/requirements/${id}`)
      .then((r) => setJob(r.data.data))
      .catch(() => navigate('/worker/jobs'))
      .finally(() => setLoading(false));
  }, [id]);

  async function handleApply() {
    setApplying(true);
    try {
      await api.post(`/requirements/${id}/apply`, { note });
      setApplied(true);
      setNoteModal(false);
      toast.success('Application submitted!');
    } catch (err) {
      if (err?.response?.status === 409) {
        setApplied(true);
        toast('You have already applied to this job.');
      }
    } finally { setApplying(false); }
  }

  if (loading) return (
    <WorkerLayout>
      <div className="flex justify-center py-20">
        <div className="animate-spin h-8 w-8 border-4 border-brand-500 border-t-transparent rounded-full" />
      </div>
    </WorkerLayout>
  );

  if (!job) return null;

  const isExpired = job.status !== 'active';
  const deadlinePassed = job.application_deadline && new Date(job.application_deadline) < new Date();

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

  return (
    <WorkerLayout>
      <div className="max-w-2xl mx-auto space-y-4 pb-24 sm:pb-6">

        {/* Header Card */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-gray-900">{job.title}</h1>
              <p className="text-sm text-gray-500 mt-0.5">{job.company_name}</p>
            </div>
            {job.urgency && (
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0 ${URGENCY_STYLE[job.urgency] || URGENCY_STYLE.medium}`}>
                {job.urgency === 'critical' ? '🔴 Urgent' : job.urgency === 'high' ? '🟡 High Priority' : job.urgency.charAt(0).toUpperCase() + job.urgency.slice(1)}
              </span>
            )}
          </div>

          {/* Quick stats */}
          <div className="grid grid-cols-2 gap-2">
            {job.wage_min && (
              <div className="bg-green-50 rounded-xl p-3 text-center">
                <p className="text-xs text-green-600">Wage</p>
                <p className="font-bold text-green-800">₹{job.wage_min}{job.wage_max ? `–${job.wage_max}` : '+'}</p>
                <p className="text-xs text-green-600">per {job.wage_type || 'day'}</p>
              </div>
            )}
            {job.number_of_workers && (
              <div className="bg-blue-50 rounded-xl p-3 text-center">
                <p className="text-xs text-blue-600">Workers Needed</p>
                <p className="font-bold text-blue-800">{job.number_of_workers}</p>
                <p className="text-xs text-blue-600">{job.applications_count || 0} applied</p>
              </div>
            )}
          </div>

          {/* Facilities */}
          {(job.accommodation || job.food_provided || job.travel_provided) && (
            <div className="flex gap-2 mt-3 flex-wrap">
              {job.accommodation   && <span className="text-xs bg-blue-50 text-blue-700 px-3 py-1 rounded-full border border-blue-100">🏠 Accommodation</span>}
              {job.food_provided   && <span className="text-xs bg-green-50 text-green-700 px-3 py-1 rounded-full border border-green-100">🍽️ Food</span>}
              {job.travel_provided && <span className="text-xs bg-purple-50 text-purple-700 px-3 py-1 rounded-full border border-purple-100">🚌 Travel</span>}
            </div>
          )}
        </div>

        {/* Details Card */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-800 mb-1">Job Details</h2>
          <div>
            <InfoRow icon="📍" label="Location" value={[job.location_text, job.district, job.state].filter(Boolean).join(', ')} />
            <InfoRow icon="📅" label="Start Date" value={fmt(job.start_date)} />
            <InfoRow icon="🏁" label="End Date" value={fmt(job.end_date)} />
            <InfoRow icon="⏰" label="Shift" value={job.shift_hours ? `${job.shift_hours} hours/day` : null} />
            <InfoRow icon="🧑‍🤝‍🧑" label="Gender Preference" value={job.gender_preference && job.gender_preference !== 'any' ? job.gender_preference : null} />
            <InfoRow icon="📂" label="Work Category" value={job.category_name} />
            <InfoRow icon="📞" label="Contact" value={job.contact_person ? `${job.contact_person}${job.contact_mobile ? ` · ${job.contact_mobile}` : ''}` : null} />
            <InfoRow icon="⏳" label="Apply Before" value={fmt(job.application_deadline)} />
          </div>
        </div>

        {/* Required Skills */}
        {job.skills?.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h2 className="font-semibold text-gray-800 mb-3">Required Skills</h2>
            <div className="flex flex-wrap gap-2">
              {job.skills.map((s) => (
                <span key={s.skill_id}
                  className={`text-xs px-3 py-1 rounded-full ${s.is_mandatory ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-600 border border-gray-200'}`}>
                  {s.skill_name}{s.is_mandatory ? ' *' : ''}
                </span>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-2">* Mandatory</p>
          </div>
        )}

        {/* Description */}
        {job.description && (
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h2 className="font-semibold text-gray-800 mb-2">About this Job</h2>
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{job.description}</p>
          </div>
        )}

        {/* Apply Section — sticky on mobile */}
        <div className="fixed bottom-16 sm:bottom-0 sm:relative left-0 right-0 sm:left-auto sm:right-auto p-4 sm:p-0 bg-white sm:bg-transparent border-t sm:border-0 border-gray-200 sm:rounded-none">
          {isExpired ? (
            <div className="bg-gray-100 text-gray-600 text-center py-3 rounded-xl text-sm font-medium">
              This job is no longer accepting applications
            </div>
          ) : deadlinePassed ? (
            <div className="bg-red-50 text-red-600 text-center py-3 rounded-xl text-sm font-medium">
              Application deadline has passed
            </div>
          ) : applied ? (
            <div className="bg-green-50 text-green-700 text-center py-3 rounded-xl text-sm font-medium border border-green-200">
              ✓ You have applied to this job
            </div>
          ) : (
            <Button fullWidth onClick={() => setNoteModal(true)} loading={applying}>
              Apply Now
            </Button>
          )}
        </div>
      </div>

      {/* Apply Note Modal */}
      {noteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-4">
            <h3 className="font-bold text-gray-900">Apply to this Job</h3>
            <p className="text-sm text-gray-500">{job.title} — {job.company_name}</p>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Add a note (optional)</label>
              <textarea value={note} onChange={(e) => setNote(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm h-24 focus:ring-2 focus:ring-brand-500 focus:outline-none resize-none"
                placeholder="Tell the employer about yourself or why you're a good fit..." />
            </div>
            <div className="flex gap-3">
              <Button variant="secondary" fullWidth onClick={() => setNoteModal(false)}>Cancel</Button>
              <Button fullWidth loading={applying} onClick={handleApply}>Submit Application</Button>
            </div>
          </div>
        </div>
      )}
    </WorkerLayout>
  );
}
